import { NetworkStatus } from '@apollo/client'
import { Currency, CurrencyAmount, Price, TradeType } from '@uniswap/sdk-core'
import {
  SupportedInterfaceChainId,
  chainIdToBackendChain,
  useIsSupportedChainId,
  useSupportedChainId,
} from 'constants/chains'
import { nativeOnChain } from 'constants/tokens'
import { PollingInterval } from 'graphql/data/util'
import useIsWindowVisible from 'hooks/useIsWindowVisible'
import useStablecoinPrice from 'hooks/useStablecoinPrice'
import { useTokenBalances } from 'hooks/useTokenBalances'
import { useMemo } from 'react'
import { ClassicTrade, INTERNAL_ROUTER_PREFERENCE_PRICE, TradeState } from 'state/routing/types'
import { useRoutingAPITrade } from 'state/routing/useRoutingAPITrade'
import { Chain, useTokenSpotPriceQuery } from 'uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks'
import { UniverseChainId } from 'uniswap/src/types/chains'
import { currencyKey } from 'utils/currencyKey'
import { getNativeTokenDBAddress } from 'utils/nativeTokens'

// ETH amounts used when calculating spot price for a given currency.
// Kept small (1 ETH) to avoid distorting prices for low-liquidity tokens
// when the KyberSwap aggregator interprets the amount as EXACT_INPUT.
function getEthAmountOut(chainId: SupportedInterfaceChainId): CurrencyAmount<Currency> {
  return CurrencyAmount.fromRawAmount(nativeOnChain(chainId), 1e18)
}

function useETHPrice(currency?: Currency): {
  data?: Price<Currency, Currency>
  isLoading: boolean
} {
  const chainId = currency?.chainId
  const isSupportedChain = useIsSupportedChainId(chainId)
  const isSupported = isSupportedChain && currency

  const amountOut = isSupported ? getEthAmountOut(chainId) : undefined
  const { trade, state } = useRoutingAPITrade(
    !isSupported /* skip */,
    TradeType.EXACT_OUTPUT,
    amountOut,
    currency,
    INTERNAL_ROUTER_PREFERENCE_PRICE,
  )

  return useMemo(() => {
    if (!isSupported) {
      return { data: undefined, isLoading: false }
    }

    if (currency?.wrapped.equals(nativeOnChain(chainId).wrapped)) {
      return {
        data: new Price(currency, currency, '1', '1'),
        isLoading: false,
      }
    }

    if (!trade || state === TradeState.LOADING) {
      return { data: undefined, isLoading: state === TradeState.LOADING }
    }

    // if initial quoting fails, we may end up with a DutchOrderTrade
    if (trade && trade instanceof ClassicTrade) {
      if (trade.kyberSwapData) {
        const price = new Price(
          currency,
          nativeOnChain(chainId),
          trade.inputAmount.quotient,
          trade.outputAmount.quotient,
        )
        return { data: price, isLoading: false }
      }
      const { numerator, denominator } = trade.routes[0].midPrice
      const price = new Price(currency, nativeOnChain(chainId), denominator, numerator)
      return { data: price, isLoading: false }
    }

    return { data: undefined, isLoading: false }
  }, [chainId, currency, isSupported, state, trade])
}

export function useUSDPrice(
  currencyAmount?: CurrencyAmount<Currency>,
  prefetchCurrency?: Currency,
): {
  data?: number
  isLoading: boolean
} {
  const currency = currencyAmount?.currency ?? prefetchCurrency
  const chainId = useSupportedChainId(currency?.chainId)
  const chain = chainIdToBackendChain({ chainId })

  // skip all pricing requests if the window is not focused
  const isWindowVisible = useIsWindowVisible()

  // PRIMARY: GraphQL token spot price by address — same source the wallet tray /
  // portfolio uses. Direct USD price avoids deriving spot through a routing
  // aggregator (Kyber), which distorts prices for low-liquidity / fee-on-transfer
  // tokens because the probe quote eats large price impact.
  const tokenDBAddress = currency
    ? currency.isNative
      ? getNativeTokenDBAddress(chain ?? Chain.Ethereum)
      : currency.wrapped.address
    : undefined
  const { data: tokenSpotData, networkStatus: tokenSpotNetworkStatus } = useTokenSpotPriceQuery({
    variables: { chain: chain ?? Chain.Ethereum, address: tokenDBAddress },
    skip: !tokenDBAddress || !chain || !isWindowVisible,
    pollInterval: PollingInterval.Normal,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
  })
  const tokenSpotUSD: number | undefined =
    tokenSpotData?.token?.project?.markets?.[0]?.price?.value ?? undefined

  // SECONDARY: Portfolio balance map. The portfolio API gives `denominatedValue.value`
  // (total USD) and `quantity` for each token the user holds — derived per-token price
  // = usdValue / balance. This is the same source the wallet tray uses, so it covers
  // tokens the spot-price endpoint doesn't index (e.g. low-liquidity tokens that
  // aren't tied to a Uniswap project).
  const { balanceMap } = useTokenBalances({ cacheOnly: true })
  const portfolioPerTokenUSD: number | undefined = useMemo(() => {
    if (tokenSpotUSD !== undefined || !currency) {
      return undefined
    }
    const entry = balanceMap[currencyKey(currency)]
    if (!entry || !entry.balance || !entry.usdValue) {
      return undefined
    }
    return entry.usdValue / entry.balance
  }, [tokenSpotUSD, currency, balanceMap])

  const useFallback = tokenSpotUSD === undefined && portfolioPerTokenUSD === undefined

  // FALLBACK 1: Token → ETH route (via KyberSwap) × ETH → USD spot. Only used when
  // GraphQL has no price for the token (e.g. unindexed token).
  const { data: tokenEthPrice, isLoading: isTokenEthPriceLoading } = useETHPrice(
    useFallback ? currency : undefined,
  )
  const isTokenEthPriced = Boolean(tokenEthPrice || isTokenEthPriceLoading)
  const { data, networkStatus } = useTokenSpotPriceQuery({
    variables: { chain: chain ?? Chain.Ethereum, address: getNativeTokenDBAddress(chain ?? Chain.Ethereum) },
    skip: !useFallback || !isTokenEthPriced || !isWindowVisible,
    pollInterval: PollingInterval.Normal,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
  })

  // FALLBACK 2: USDC-based pricing for chains not yet supported by backend.
  const { price: stablecoinPrice } = useStablecoinPrice(
    useFallback && !isTokenEthPriced ? currency : undefined,
  )

  return useMemo(() => {
    if (!currencyAmount) {
      return { data: undefined, isLoading: false }
    }
    if (tokenSpotUSD !== undefined) {
      return {
        data: parseFloat(currencyAmount.toExact()) * tokenSpotUSD,
        isLoading: false,
      }
    }
    if (portfolioPerTokenUSD !== undefined) {
      return {
        data: parseFloat(currencyAmount.toExact()) * portfolioPerTokenUSD,
        isLoading: false,
      }
    }
    if (stablecoinPrice) {
      return { data: parseFloat(stablecoinPrice.quote(currencyAmount).toSignificant()), isLoading: false }
    }
    const ethUSDPrice = data?.token?.project?.markets?.[0]?.price?.value
    if (ethUSDPrice && tokenEthPrice) {
      return { data: parseFloat(tokenEthPrice.quote(currencyAmount).toExact()) * ethUSDPrice, isLoading: false }
    }
    return {
      data: undefined,
      isLoading:
        isTokenEthPriceLoading ||
        networkStatus === NetworkStatus.loading ||
        tokenSpotNetworkStatus === NetworkStatus.loading,
    }
  }, [
    currencyAmount,
    tokenSpotUSD,
    portfolioPerTokenUSD,
    stablecoinPrice,
    data?.token?.project?.markets,
    tokenEthPrice,
    isTokenEthPriceLoading,
    networkStatus,
    tokenSpotNetworkStatus,
  ])
}
